import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { pushOrderSuccess, pushGA4Purchase } from '../utils/gtm';
import { mapOrderStatusForDisplay } from '../utils/orderStatusDisplay';

/** Mirrors POST /api/orders response.diagnostics email fields when present */
interface OrderEmailDiagnostics {
  emailSent: boolean;
  emailError: string | null;
  emailMessageId?: string | null;
}

interface OrderSuccessState {
  orderNumber: string;
  orderId?: string;
  orderStatus?: string;
  externalOrderId?: string | null;
  externalOrderStatus?: string | null;
  externalApiResponse?: any;
  email: string;
  product: string;
  total: number;
  /** Subtotal (excludes tax/shipping) for AddShoppers conversion tracking */
  subtotal?: number;
  /** Promo/offer code applied at checkout */
  offerCode?: string;
  placedAt?: string;
  /** Set from API diagnostics when checkout knows whether confirmation email was sent */
  emailDiagnostics?: OrderEmailDiagnostics;
}

const OrderSuccessPage: React.FC = () => {
  const location = useLocation();
  const stateData = location.state as OrderSuccessState | null;

  const defaultOrderNumber = 'ALT-' + Math.random().toString(36).substring(2, 11).toUpperCase();
  const data: OrderSuccessState = stateData || {
    orderNumber: defaultOrderNumber,
    orderId: defaultOrderNumber,
    orderStatus: 'pending',
    externalOrderId: null,
    externalOrderStatus: null,
    externalApiResponse: null,
    email: 'customer@example.com',
    product: 'Alert1 Medical Alert System',
    total: 0,
    placedAt: new Date().toISOString()
  };

  const orderStatus = (data.orderStatus || data.externalOrderStatus || 'pending').toLowerCase();
  const isPending = ['pending', 'processing', 'submitted', 'hold'].includes(orderStatus);
  const isCompleted = ['complete', 'completed', 'approved', 'success'].includes(orderStatus);

  const emailFailed =
    data.emailDiagnostics != null && data.emailDiagnostics.emailSent === false;

  const statusHeading = isPending
    ? 'Your order is being finalized'
    : isCompleted
      ? 'Your order is confirmed!'
      : 'Order received';

  const statusDescription = emailFailed
    ? isPending
      ? 'We successfully received your order and we are confirming your payment right now. We could not send a confirmation email automatically—see the notice below and contact Member Care if you need a copy of your receipt.'
      : isCompleted
        ? 'Payment confirmation is complete and your order is queued for fulfillment. We could not send a confirmation email automatically—see the notice below.'
        : 'We received your order details. We could not send a confirmation email automatically—see the notice below and contact Member Care if you need help.'
    : isPending
      ? 'We successfully received your order and we are confirming your payment right now. You will receive a confirmation email as soon as billing completes. This usually takes less than a minute.'
      : isCompleted
        ? 'Payment confirmation is complete and your order is queued for fulfillment. You will receive a confirmation email with tracking details shortly.'
        : 'We received your order details. If you do not receive an email confirmation within a few minutes, please contact our Member Care team.';

  const external = data.externalApiResponse;
  const externalOrderId = data.externalOrderId || external?.orderID || external?.orderId || null;
  const externalAccountId = external?.accountID || external?.accountId || null;
  const externalPaymentStatus = external?.orderStatus || data.externalOrderStatus || data.orderStatus || 'pending';
  const paymentPending = ['pending', 'processing', 'submitted', 'new', 'hold'].includes(String(externalPaymentStatus).toLowerCase());

  const placedDate = data.placedAt ? new Date(data.placedAt) : new Date();
  const placedDateDisplay = placedDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[OrderSuccess] Status sources:', {
        dataOrderStatus: data.orderStatus,
        dataExternalOrderStatus: data.externalOrderStatus,
        externalOrderStatus: external?.orderStatus,
        statusForDisplay: mapOrderStatusForDisplay(data.orderStatus || data.externalOrderStatus || 'pending'),
        externalPaymentStatus,
        gatewayStatusForDisplay: mapOrderStatusForDisplay(externalPaymentStatus),
      });
    }
  }, [data.orderStatus, data.externalOrderStatus, external?.orderStatus, externalPaymentStatus]);

  // Push AddShoppers and GA4 conversion events to dataLayer for GTM
  useEffect(() => {
    const orderId = data.orderId || data.orderNumber;
    if (!orderId || !data.email) return;

    const addshoppersValue = data.subtotal != null ? data.subtotal : data.total;
    const ga4Value = data.total;

    // 1. AddShoppers/Minty format
    pushOrderSuccess({
      orderId: String(orderId),
      orderSubtotal: Number(addshoppersValue),
      currency: 'USD',
      userEmail: data.email,
      ...(data.offerCode && { offerCode: data.offerCode }),
    });

    // 2. GA4/Google Ads format
    pushGA4Purchase({
      transactionId: String(orderId),
      value: Number(ga4Value),
      currency: 'USD',
      ...(data.offerCode && { coupon: data.offerCode }),
      items: [{
        item_id: String(orderId),
        item_name: data.product,
        price: Number(ga4Value),
        quantity: 1,
        index: 0,
      }],
    });
  }, [data.orderId, data.orderNumber, data.email, data.subtotal, data.total, data.product, data.offerCode]);

  return (
    <div className="order-success-page">
      <div className="success-container">
        <div className="success-icon">✓</div>
        <h1>Thank You for Your Order!</h1>
        <p className="success-message">{statusHeading}</p>
        <p className="status-description">{statusDescription}</p>
        
        <div className="order-details-box">
          <h2>Order Confirmation</h2>
          <div className="detail-row">
            <span className="detail-label">Order Number:</span>
            <span className="detail-value">{data.orderNumber}</span>
          </div>
          {data.orderId && (
            <div className="detail-row">
              <span className="detail-label">Internal Order ID:</span>
              <span className="detail-value">{data.orderId}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Status:</span>
            <span className="detail-value">{mapOrderStatusForDisplay(data.orderStatus || data.externalOrderStatus || 'pending')}</span>
          </div>
          {externalOrderId && (
            <div className="detail-row">
              <span className="detail-label">Gateway Order ID:</span>
              <span className="detail-value">{externalOrderId}</span>
            </div>
          )}
          {externalAccountId && (
            <div className="detail-row">
              <span className="detail-label">Gateway Account ID:</span>
              <span className="detail-value">{externalAccountId}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Gateway Status:</span>
            <span className="detail-value">{mapOrderStatusForDisplay(externalPaymentStatus)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Product:</span>
            <span className="detail-value">{data.product}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Total:</span>
            <span className="detail-value">${data.total.toFixed(2)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Placed:</span>
            <span className="detail-value">{placedDateDisplay}</span>
          </div>
        </div>
        
        {emailFailed ? (
          <div className="order-success-email-warning" role="alert">
            <h3 className="order-success-email-warning-title">Confirmation email</h3>
            <p>
              Your order was placed successfully, but we were <strong>unable to send a confirmation email</strong> to{' '}
              <strong>{data.email}</strong>. Please save your order number above and contact Member Care at{' '}
              <a href="tel:1-877-579-9862">1-877-579-9862</a> if you need a receipt or have questions.
            </p>
            <p>Your order is still on file; this message usually means a temporary email system issue on our side.</p>
            {data.emailDiagnostics?.emailError ? (
              <details className="order-success-email-warning-details">
                <summary>Details for support</summary>
                <p>{data.emailDiagnostics.emailError}</p>
                {data.emailDiagnostics.emailMessageId ? (
                  <p className="order-success-email-warning-meta">Message ID: {data.emailDiagnostics.emailMessageId}</p>
                ) : null}
              </details>
            ) : null}
          </div>
        ) : (
          <div className="email-confirmation">
            <p>A confirmation email has been sent to <strong>{data.email}</strong></p>
            <p>Please check your inbox for order details and tracking information in the next few minutes.</p>
            {(isPending || paymentPending) && (
              <p>If you do not receive a confirmation email within 15 minutes, please call us so we can complete the activation for you.</p>
            )}
          </div>
        )}
        
        {external && (
          <div className="external-api-summary">
            <h3>Gateway Response Snapshot</h3>
            <div className="detail-row">
              <span className="detail-label">Order Status:</span>
              <span className="detail-value">{mapOrderStatusForDisplay(external.orderStatus)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Grand Total:</span>
              <span className="detail-value">{external.orderTotals?.grandTotal ?? 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Paid:</span>
              <span className="detail-value">{external.orderTotals?.paid ?? 'N/A'}</span>
            </div>
            <details className="external-api-details">
              <summary>View full gateway response</summary>
              <pre>{JSON.stringify(external, null, 2)}</pre>
            </details>
          </div>
        )}

        <div className="next-steps">
          <h3>What's Next?</h3>
          <ul>
            <li>You will receive a welcome email with setup instructions</li>
            <li>Your medical alert system will ship within 1-2 business days</li>
            <li>Our team will contact you to schedule installation and testing</li>
          </ul>
        </div>
        
        <div className="action-buttons">
          <Link to="/" className="btn btn-primary">Return to Home</Link>
          <Link to="/my-account" className="btn btn-secondary">View My Account</Link>
        </div>
        
        <div className="contact-support">
          <p>Questions? Call us at <a href="tel:1-877-579-9862">1-877-579-9862</a></p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default OrderSuccessPage;

